import type { CityConfig } from "../city-template/types";

export const puebloConfig: CityConfig = {
  city: "Pueblo",
  state: "Colorado",
  stateAbbr: "CO",
  slug: "pueblo",
  route: "/pueblo",
  title: "Emergency Passport Pueblo, CO — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Pueblo, CO — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Pueblo County and southern Colorado travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Pueblo, CO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Pueblo and southern Colorado travelers get their expedited passports as quickly as 24 hours. The regional agency is 100 miles away in Aurora — we bring the solution to you. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Pueblo%20Colorado%20cityscape%20with%20the%20Arkansas%20River%20flowing%20through%20downtown%2C%20historic%20Union%20Avenue%20district%20with%20Victorian%20architecture%2C%20Sangre%20de%20Cristo%20mountains%20visible%20in%20the%20distance%2C%20warm%20golden%20sunset%20light%20over%20the%20southern%20Colorado%20plains%2C%20professional%20cityscape%20photography%2C%20warm%20amber%20and%20golden%20tones%2C%20no%20visible%20text%20or%20people%2C%20cinematic%20wide%20angle%20view&width=1800&height=700&seq=pueblo-hero-001&orientation=landscape",
  heroImageAlt: "Pueblo Colorado cityscape with Arkansas River and Sangre de Cristo mountains in the distance",
  geoPosition: "38.2544;-104.6091",
  geoPlacename: "Pueblo, Colorado",
  lastModified: "2026-06-03",
  regionalAgency: {
    name: "Colorado Passport Agency",
    address: "7245 S Havana Street Suite B-600, Centennial, CO 80112",
    distance: "Approximately 100 miles north — about 1.5 hours via I-25",
  },
  nearbyCitiesZips: [
    { name: "Downtown Pueblo", zips: "81003, 81004" },
    { name: "Pueblo West", zips: "81007" },
    { name: "South Pueblo", zips: "81005, 81006" },
    { name: "East Pueblo", zips: "81001, 81008" },
    { name: "Belmont", zips: "81008" },
    { name: "Canon City", zips: "81212" },
    { name: "Florence", zips: "81226" },
    { name: "Walsenburg", zips: "81089" },
    { name: "La Junta", zips: "81050" },
    { name: "Rye", zips: "81069" },
    { name: "Beulah", zips: "81023" },
    { name: "Boone", zips: "81025" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Pueblo departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Colorado Springs", route: "/colorado-springs" },
    { name: "Emergency passport help in Denver", route: "/denver" },
    { name: "Urgent passport services in Boulder", route: "/boulder" },
    { name: "Same-day passport help in Canon City", route: "/pueblo" },
    { name: "Expedited passport services in Pueblo West", route: "/pueblo" },
    { name: "Passport courier services in Walsenburg", route: "/pueblo" },
    { name: "Rush passport help in La Junta", route: "/pueblo" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Pueblo remotely via secure courier. We do not have a walk-in office in Pueblo — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Pueblo and southern Colorado travelers without the 100-mile drive to Aurora.",
  fpcOfficeAddress: "7245 S Havana Street Suite B-600, Centennial, CO 80112",
  fpcOfficeDistance: "Serving all of Pueblo County and southern Colorado",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Pueblo County and southern Colorado. Pueblo is the largest city in southern Colorado, and the nearest regional passport agency is 100 miles north in Aurora. Our registered courier service eliminates that drive entirely — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Pueblo neighborhoods and nearby communities including Pueblo West, Canon City, Florence, Walsenburg, and La Junta. If you are in Pueblo County or southern Colorado, we have you covered.",
  howItWorksIntro: "Most Pueblo travelers complete everything online and ship documents overnight. No need to drive 100 miles to Aurora or wait for a scarce passport agency appointment. Our emergency and expedited passport process is designed for Pueblo travelers who need fast, reliable results.",
  mistakesIntro: "With the Colorado Passport Agency 100 miles away, Pueblo travelers can't afford to make mistakes that require a second trip or a rejected application. Here are the most common errors we see.",
  testimonialsIntro: "Pueblo travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Pueblo? Here are the most common ones we hear from southern Colorado travelers.",
  statePageRoute: "/states/colorado",
  statePageName: "Colorado",
  dmvWebsite: "https://dmv.colorado.gov",
  dmvLabel: "Colorado DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours without the 100-mile drive to Aurora.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Carlos M.",
      location: "Downtown Pueblo, CO",
      rating: 5,
      text: "My family had a trip to Mexico planned and I realized my passport expired the week before. Fast Passport Center got my renewal done in 48 hours. No need to drive to Aurora. Incredible service.",
      service: "Emergency Renewal",
    },
    {
      name: "Jennifer T.",
      location: "Pueblo West, CO",
      rating: 5,
      text: "Needed a new passport for my daughter before a study abroad program. Fast Passport Center guided us through the entire process and had it ready in 6 days. Perfect.",
      service: "Child Passport",
    },
    {
      name: "Robert W.",
      location: "Belmont, Pueblo",
      rating: 5,
      text: "Lost my passport before a conference in London. They handled everything — DS-64 form, replacement application, the works — and had it done in 2 days. Saved my trip.",
      service: "Lost Passport",
    },
    {
      name: "Dr. Anna L.",
      location: "Canon City, CO",
      rating: 5,
      text: "As a researcher traveling internationally for fieldwork, I can't wait months. Fast Passport Center's expedited service has been reliable every single time. Much better than driving to Denver.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "pue-1",
      name: "Pueblo Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "421 N. Main St",
      city: "Pueblo",
      state: "CO",
      zip: "81003",
      phone: "(719) 544-5551",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Pueblo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "pue-2",
      name: "Pueblo West Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "153 S. McCulloch Blvd",
      city: "Pueblo West",
      state: "CO",
      zip: "81007",
      phone: "(719) 547-9622",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Pueblo West Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Pueblo West residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "pue-3",
      name: "Canon City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "512 Main St",
      city: "Canon City",
      state: "CO",
      zip: "81212",
      phone: "(719) 275-2571",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Canon City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Fremont County residents. Limited passport appointment slots available.",
      icon: "ri-mail-line",
    },
    {
      id: "pue-4",
      name: "La Junta Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "612 Santa Fe Ave",
      city: "La Junta",
      state: "CO",
      zip: "81050",
      phone: "(719) 384-5666",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown La Junta",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "pue-5",
      name: "Walsenburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "515 W. 7th St",
      city: "Walsenburg",
      state: "CO",
      zip: "81089",
      phone: "(719) 738-2060",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "0.3 miles from downtown Walsenburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Huerfano County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "pue-6",
      name: "Pueblo County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "215 W. 10th St",
      city: "Pueblo",
      state: "CO",
      zip: "81003",
      phone: "(719) 583-6520",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Pueblo City Hall",
      website: "https://www.pueblocounty.us/",
      websiteLabel: "Pueblo County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Aurora (100 mi)", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Pueblo photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background. Getting this wrong means a 100-mile drive to Aurora was wasted.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Pueblo acceptance facilities. One error means rescheduling weeks out.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Pueblo applicants show up with incorrect checks and have to reschedule their appointment entirely.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments — after a 100-mile drive from Pueblo.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Pueblo families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Pueblo travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel — especially given the distance to the regional agency.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Pueblo." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Pueblo address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Pueblo?",
      a: "Fast Passport Center offers emergency passport processing in Pueblo in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Pueblo travelers with urgent international flights — without the 100-mile drive to Aurora.",
    },
    {
      q: "Do I have to drive to Aurora to get a fast passport from Pueblo?",
      a: "No — with Fast Passport Center, you complete everything online and ship your documents overnight from Pueblo. Our couriers handle the in-person delivery to State Department facilities. The 100-mile drive to Aurora is completely unnecessary.",
    },
    {
      q: "Can I get a same-day passport in Pueblo?",
      a: "Same-day passports are not available in Pueblo. The closest regional passport agency is in Aurora, 100 miles north. For urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Pueblo residents without making that drive.",
    },
    {
      q: "Where can I get a fast passport renewal in Pueblo?",
      a: "Fast Passport Center offers fast passport renewal in Pueblo with no office visit required. We serve all Pueblo neighborhoods and zip codes. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Pueblo?",
      a: "The fastest way to get an urgent passport in Pueblo is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround.",
    },
    {
      q: "What if I live in nearby Canon City, Walsenburg, or La Junta?",
      a: "We serve all of Pueblo County and surrounding areas including Canon City, Florence, Walsenburg, and La Junta. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Pueblo?",
      a: "Popular options include Pueblo Main Post Office (81003), Pueblo West Post Office (81007), Canon City Post Office (81212), and Pueblo County Clerk (81003). All require appointments.",
    },
    {
      q: "How much does expedited passport service cost in Pueblo?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Pueblo?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Pueblo offer passport photo services. We review your photos before submission.",
    },
    {
      q: "What documents do I need for a first-time passport in Pueblo?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist.",
    },
    {
      q: "Can you help with a child's passport in Pueblo?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Pueblo families through the entire process.",
    },
    {
      q: "Is Fast Passport Center cheaper than going to the regional agency in Aurora?",
      a: "Yes — especially when factoring in the cost of a 200-mile round trip to Aurora, potential hotel stay, lost work time, and the risk of a rejected application requiring a second trip. Our best price guarantee means we're typically 30–100% less expensive than FedEx, Staples, and other third-party resellers.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Pueblo", address: "2726 N. Elizabeth St, Pueblo, CO 81008", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Downtown Pueblo", address: "201 S. Santa Fe Ave, Pueblo, CO 81003", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Pueblo", address: "3640 Dillon Dr, Pueblo, CO 81008", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Pueblo", address: "2626 S. Prairie Ave, Pueblo, CO 81005", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Pueblo West", address: "240 S. McCulloch Blvd, Pueblo West, CO 81007", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Pueblo West", address: "895 S. McCulloch Blvd, Pueblo West, CO 81007", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Canon City", address: "1830 Fremont Dr, Canon City, CO 81212", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Pueblo", address: "3301 Dillon Dr, Pueblo, CO 81008", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Pueblo",
      address: "301 N. 4th St, Pueblo, CO 81003",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Pueblo West",
      address: "153 S. McCulloch Blvd, Pueblo West, CO 81007",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Convenient for Pueblo West residents. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Canon City",
      address: "609 Macon Ave, Canon City, CO 81212",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Fremont County. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Walsenburg",
      address: "515 W. 7th St, Walsenburg, CO 81089",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Huerfano County full-service center.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PUB",
      name: "Pueblo Memorial Airport",
      distance: "3 miles from downtown Pueblo",
      address: "31201 Bryan Circle, Pueblo, CO 81001",
      note: "Regional airport for Pueblo with limited commercial service. Most international travelers connect through Denver.",
      flightUrl: "https://www.puebloairport.com/",
      flightLabel: "Pueblo Airport",
      featured: true,
    },
    {
      code: "DEN",
      name: "Denver International Airport",
      distance: "100 miles from Pueblo via I-25",
      address: "8500 Peña Blvd, Denver, CO 80249",
      note: "Primary international airport for Pueblo travelers. Major hub for international flights to Europe, Asia, and Latin America.",
      flightUrl: "https://www.flydenver.com/",
      flightLabel: "DEN Flight Info",
      featured: false,
    },
  ],
};